import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Landmark, ShieldCheck, Scale, SearchCheck, Route, BadgeCheck,
  FileText, Baby, Award, Users, CheckCircle2, ArrowRight, Paperclip,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { icon: Landmark, title: 'AI Governance', desc: 'Develop governance frameworks that prepare your organisation for future regulation.' },
  { icon: ShieldCheck, title: 'AI Safety Assessments', desc: 'Identify operational, technical and reputational risks before they become problems.' },
  { icon: Scale, title: 'Regulatory Readiness', desc: 'Prepare for emerging AI legislation, industry standards and international compliance.' },
  { icon: SearchCheck, title: 'Product Review', desc: 'Independent review of AI-enabled products before market launch.' },
  { icon: Route, title: 'Responsible AI Strategy', desc: 'Develop long-term AI roadmaps aligned with business objectives.' },
  { icon: BadgeCheck, title: 'Risk & Assurance', desc: 'Independent assurance programs for boards, executives and investors.' },
  { icon: FileText, title: 'AI Policy Development', desc: 'Internal policies for employees, developers and executive teams.' },
  { icon: Baby, title: 'Child Safety & Digital Wellbeing', desc: 'Specialist advisory relating to AI systems involving children and vulnerable users.' },
  { icon: Award, title: 'Certification Preparation', desc: 'Prepare organisations and products for future SAFE Standard certification pathways.' },
  { icon: Users, title: 'Executive & Board Advisory', desc: 'Strategic advice for directors, investors and executive leadership teams.' },
];

const PROCESS = [
  { n: '1', title: 'Initial Enquiry', desc: 'Tell us about your organisation, project and objectives.' },
  { n: '2', title: 'Discovery Review', desc: 'Our team reviews your submission, supporting material and publicly available information.' },
  { n: '3', title: 'Internal Assessment', desc: 'We conduct an initial strategic assessment and identify opportunities, risks and likely engagement scope.' },
  { n: '4', title: 'Proposal', desc: 'You receive a tailored proposal outlining objectives, recommended approach, expected outcomes, timeframes and commercial proposal.' },
  { n: '5', title: 'Advisory Engagement', desc: 'Work directly with SAFE Standard specialists throughout the engagement.' },
];

const TYPICAL_ENGAGEMENTS = [
  'AI Startup Review', 'AI Governance Framework', 'Enterprise AI Risk Assessment', 'Government Advisory',
  'AI Procurement Due Diligence', 'AI Product Safety Review', 'AI Policy Development', 'Board Workshops',
  'AI Readiness Assessment', 'Child Safety Assessment', 'Responsible AI Strategy', 'Regulatory Gap Analysis',
];

const WHO_WE_WORK_WITH = [
  'Technology Companies', 'Startups', 'Government', 'Education', 'Healthcare', 'Financial Services',
  'Defence', 'Mining', 'Manufacturing', 'Enterprise', 'Not-for-profits', 'Investors', 'Boards', 'Regulators',
];

const PRICING_FACTORS = [
  'Project complexity', 'Size of organisation', 'Required expertise',
  'Timeframe', 'Number of stakeholders', 'Deliverables required',
];

const STAGES = ['Exploring AI', 'Prototype', 'Pilot', 'Internal Deployment', 'Commercial Product', 'Enterprise Rollout', 'Government Program', 'Existing AI Product', 'Other'];
const OUTCOMES = ['Governance', 'Risk Assessment', 'Compliance', 'Product Review', 'Certification Preparation', 'AI Strategy', 'Child Safety', 'Executive Advisory', 'Policy Development', 'Other'];
const TIMEFRAMES = ['Immediate', 'Within 30 Days', '1–3 Months', '3–6 Months', 'Longer Term'];
const BUDGETS = ['Under USD $5,000', 'USD $5,000–10,000', 'USD $10,000–25,000', 'USD $25,000–50,000', 'USD $50,000–100,000', 'USD $100,000+', 'Prefer to discuss'];

const initialForm = {
  org_name: '', website: '', industry: '', country: '', num_employees: '',
  primary_contact: '', email: '', phone: '',
  org_description: '', ai_project: '', current_stage: '',
  challenge: '', success_definition: '',
  desired_outcomes: [],
  link_website: '', link_product: '', link_github: '', link_demo: '', link_documentation: '',
  timeframe: '', budget: '', additional_info: '',
  consent_no_engagement: false, consent_review: false,
};

const inputClass = 'w-full px-4 py-3 border border-slate-300 text-sm focus:outline-none focus:border-slate-900 transition-colors bg-white';

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

const AdvisoryPage = () => {
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const toggleOutcome = (value) => {
    setFormData((prev) => ({
      ...prev,
      desired_outcomes: prev.desired_outcomes.includes(value)
        ? prev.desired_outcomes.filter((o) => o !== value)
        : [...prev.desired_outcomes, value],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent_no_engagement || !formData.consent_review) {
      toast.error('Please confirm both consent statements to submit.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/forms/advisory`, formData);
      if (res.data?.success) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        toast.error('Submission failed. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-slate-50 min-h-screen" data-testid="advisory-success">
        <section className="bg-slate-900 text-white py-20 md:py-28">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <CheckCircle2 className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
            <p className="text-sm text-cyan-400 uppercase tracking-widest font-semibold mb-3">Enquiry Received</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              Thank you — your enquiry is with our team.
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              We&rsquo;ll begin a discovery review of your submission and be in touch to discuss next steps.
              A confirmation has been sent to your email. Submitting this form does not create an engagement.
            </p>
          </div>
        </section>
        <section className="py-12 text-center space-x-4">
          <Link to="/" className="bg-slate-900 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors inline-block" data-testid="advisory-home-link">
            Return home
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="advisory-page">
      {/* HERO */}
      <section className="bg-slate-900 text-white py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="text-sm text-cyan-400 uppercase tracking-widest font-semibold mb-4">Strategic Advisory & AI Assurance</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-8">
            Strategic Advisory for Responsible AI
          </h1>
          <div className="space-y-5 text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl">
            <p>
              Whether you&rsquo;re building an AI product, deploying AI within your organisation, preparing for
              regulation, or responding to emerging risks, SAFE Standard provides independent strategic advisory
              services to help organisations move faster, reduce risk, and build trust.
            </p>
            <p>Every engagement is tailored to your organisation, objectives and stage of maturity.</p>
            <p className="text-slate-400 text-base">
              No two AI projects are the same, so every engagement begins with an assessment before recommendations are made.
            </p>
          </div>
          <a href="#advisory-form" className="mt-10 inline-flex items-center gap-2 bg-cyan-600 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-cyan-500 transition-colors" data-testid="advisory-hero-cta">
            Request Strategic Advisory <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* WHY WORK WITH SAFE */}
      <section className="py-20 md:py-28 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-8">Why Work With SAFE Standard?</h2>
          <div className="space-y-5 text-base md:text-lg text-slate-700 leading-relaxed">
            <p>
              Unlike traditional consulting firms, SAFE Standard was established specifically to address one of the
              world&rsquo;s biggest emerging challenges:
            </p>
            <p className="text-slate-900 font-semibold border-l-2 border-cyan-600 pl-5">
              How do organisations deploy AI safely, responsibly and with confidence?
            </p>
            <p>
              Our founders, researchers and international advisors work across AI governance, safety, regulation,
              standards development, certification, technical implementation and organisational strategy.
            </p>
            <p>
              Whether you&rsquo;re a startup or a multinational organisation, we help translate complex AI risk into
              practical action.
            </p>
          </div>
        </div>
      </section>

      {/* HOW WE CAN HELP */}
      <section className="py-20 md:py-28 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-12">How We Can Help</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="bg-white p-8 hover:bg-slate-50 transition-colors group" data-testid={`advisory-service-${s.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <Icon className="w-8 h-8 text-cyan-700 mb-5" strokeWidth={1.5} />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT PROCESS */}
      <section className="py-20 md:py-28 border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4">Engagement Process</h2>
          <p className="text-base text-slate-500 mb-12 max-w-2xl">A structured pathway from first contact to active engagement.</p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-0 md:gap-6">
            {PROCESS.map((step, i) => (
              <div key={step.n} className="relative pt-8 md:pt-0" data-testid={`advisory-process-${step.n}`}>
                <div className="flex md:block items-center gap-4">
                  <div className="w-12 h-12 flex-shrink-0 bg-slate-900 text-white flex items-center justify-center text-lg font-bold">
                    {step.n}
                  </div>
                  <div className="hidden md:block h-px bg-slate-200 w-full mt-6 mb-6" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mt-4 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
                {i < PROCESS.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-3 -right-4 w-5 h-5 text-slate-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TYPICAL ENGAGEMENTS + WHO WE WORK WITH */}
      <section className="py-20 md:py-28 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-3">Typical Engagements</h2>
            <p className="text-sm text-slate-500 mb-8">Examples only.</p>
            <div className="flex flex-wrap gap-2">
              {TYPICAL_ENGAGEMENTS.map((e) => (
                <span key={e} className="text-sm text-slate-700 bg-white border border-slate-300 px-4 py-2">{e}</span>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-3">Who We Work With</h2>
            <p className="text-sm text-slate-500 mb-8">We support organisations including:</p>
            <div className="flex flex-wrap gap-2">
              {WHO_WE_WORK_WITH.map((w) => (
                <span key={w} className="text-sm text-slate-700 bg-white border border-slate-300 px-4 py-2">{w}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EVERY ENGAGEMENT IS DIFFERENT */}
      <section className="py-20 md:py-28 border-b border-slate-200 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">Every Engagement Is Different</h2>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-10 max-w-3xl">
            Rather than fixed pricing, each engagement is scoped individually based on:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {PRICING_FACTORS.map((f) => (
              <div key={f} className="flex items-center gap-3 border border-slate-700 px-5 py-4">
                <span className="w-1.5 h-1.5 bg-cyan-400 flex-shrink-0" />
                <span className="text-sm text-slate-200">{f}</span>
              </div>
            ))}
          </div>
          <p className="text-base text-slate-400 leading-relaxed max-w-3xl">
            This allows us to provide recommendations that are proportionate, practical and commercially realistic.
          </p>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="advisory-form" className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="text-sm text-cyan-700 uppercase tracking-widest font-semibold mb-3">Request Strategic Advisory</p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-3">Tell us about your organisation and AI project.</h2>
          <p className="text-base text-slate-500 mb-12">
            The more context you provide, the better our discovery review. Fields marked with * are required.
          </p>

          <form onSubmit={handleSubmit} className="space-y-10" data-testid="advisory-form">
            {/* ORGANISATION DETAILS */}
            <div className="bg-white border border-slate-200 p-6 md:p-8">
              <h3 className="text-lg font-bold text-slate-900 mb-6">Organisation Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Organisation name" required>
                  <input name="org_name" required value={formData.org_name} onChange={handleChange} className={inputClass} data-testid="adv-org-name" />
                </Field>
                <Field label="Website">
                  <input name="website" value={formData.website} onChange={handleChange} className={inputClass} placeholder="https://..." data-testid="adv-website" />
                </Field>
                <Field label="Industry">
                  <input name="industry" value={formData.industry} onChange={handleChange} className={inputClass} data-testid="adv-industry" />
                </Field>
                <Field label="Country">
                  <input name="country" value={formData.country} onChange={handleChange} className={inputClass} data-testid="adv-country" />
                </Field>
                <Field label="Number of employees">
                  <input name="num_employees" value={formData.num_employees} onChange={handleChange} className={inputClass} data-testid="adv-employees" />
                </Field>
                <Field label="Primary contact" required>
                  <input name="primary_contact" required value={formData.primary_contact} onChange={handleChange} className={inputClass} data-testid="adv-contact" />
                </Field>
                <Field label="Email" required>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className={inputClass} data-testid="adv-email" />
                </Field>
                <Field label="Phone">
                  <input name="phone" value={formData.phone} onChange={handleChange} className={inputClass} data-testid="adv-phone" />
                </Field>
              </div>
            </div>

            {/* ABOUT + PROJECT */}
            <div className="bg-white border border-slate-200 p-6 md:p-8 space-y-5">
              <h3 className="text-lg font-bold text-slate-900 mb-2">About Your Organisation & AI Project</h3>
              <Field label="What does your organisation do?">
                <textarea name="org_description" rows={3} value={formData.org_description} onChange={handleChange} className={inputClass} data-testid="adv-org-desc" />
              </Field>
              <Field label="Describe the AI system, product or initiative">
                <textarea name="ai_project" rows={3} value={formData.ai_project} onChange={handleChange} className={inputClass} data-testid="adv-ai-project" />
              </Field>
              <Field label="Current stage">
                <select name="current_stage" value={formData.current_stage} onChange={handleChange} className={inputClass} data-testid="adv-stage">
                  <option value="">Select a stage…</option>
                  {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="What challenge are you facing?">
                <textarea name="challenge" rows={4} value={formData.challenge} onChange={handleChange} className={inputClass} data-testid="adv-challenge" />
              </Field>
              <Field label="What does success look like?">
                <textarea name="success_definition" rows={4} value={formData.success_definition} onChange={handleChange} className={inputClass} data-testid="adv-success" />
              </Field>
            </div>

            {/* OUTCOMES */}
            <div className="bg-white border border-slate-200 p-6 md:p-8">
              <h3 className="text-lg font-bold text-slate-900 mb-2">What Outcomes Are You Hoping To Achieve?</h3>
              <p className="text-sm text-slate-500 mb-6">Select all that apply.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {OUTCOMES.map((o) => {
                  const checked = formData.desired_outcomes.includes(o);
                  return (
                    <label key={o} className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${checked ? 'border-slate-900 bg-slate-50' : 'border-slate-300 hover:border-slate-400'}`} data-testid={`adv-outcome-${o.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleOutcome(o)} className="w-4 h-4 accent-slate-900" />
                      <span className="text-sm text-slate-700">{o}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* DOCUMENTATION + LINKS */}
            <div className="bg-white border border-slate-200 p-6 md:p-8 space-y-5">
              <h3 className="text-lg font-bold text-slate-900 mb-2">Supporting Material</h3>
              <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 p-4">
                <Paperclip className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-600 leading-relaxed">
                  Have existing documentation — PDFs, Word, PowerPoint, policies, architecture, product documentation,
                  risk registers or whitepapers? Once you&rsquo;ve submitted this enquiry, you can email supporting
                  material to <a href="mailto:safe@safestandard.ai" className="font-semibold text-slate-900 underline underline-offset-2">safe@safestandard.ai</a> and we&rsquo;ll add it to your review.
                </p>
              </div>
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Relevant Links</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Website">
                  <input name="link_website" value={formData.link_website} onChange={handleChange} className={inputClass} placeholder="https://..." data-testid="adv-link-website" />
                </Field>
                <Field label="Product URL">
                  <input name="link_product" value={formData.link_product} onChange={handleChange} className={inputClass} placeholder="https://..." data-testid="adv-link-product" />
                </Field>
                <Field label="GitHub">
                  <input name="link_github" value={formData.link_github} onChange={handleChange} className={inputClass} placeholder="https://..." data-testid="adv-link-github" />
                </Field>
                <Field label="Demo">
                  <input name="link_demo" value={formData.link_demo} onChange={handleChange} className={inputClass} placeholder="https://..." data-testid="adv-link-demo" />
                </Field>
                <Field label="Documentation">
                  <input name="link_documentation" value={formData.link_documentation} onChange={handleChange} className={inputClass} placeholder="https://..." data-testid="adv-link-docs" />
                </Field>
              </div>
            </div>

            {/* TIMEFRAME + BUDGET */}
            <div className="bg-white border border-slate-200 p-6 md:p-8">
              <h3 className="text-lg font-bold text-slate-900 mb-6">Timeframe & Budget</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Project timeframe">
                  <select name="timeframe" value={formData.timeframe} onChange={handleChange} className={inputClass} data-testid="adv-timeframe">
                    <option value="">Select a timeframe…</option>
                    {TIMEFRAMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Estimated budget">
                  <select name="budget" value={formData.budget} onChange={handleChange} className={inputClass} data-testid="adv-budget">
                    <option value="">Select a budget…</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Additional information">
                  <textarea name="additional_info" rows={4} value={formData.additional_info} onChange={handleChange} className={inputClass} data-testid="adv-additional" />
                </Field>
              </div>
            </div>

            {/* CONSENT */}
            <div className="bg-white border border-slate-200 p-6 md:p-8 space-y-4">
              <h3 className="text-lg font-bold text-slate-900 mb-2">Consent</h3>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="adv-consent-1">
                <input type="checkbox" checked={formData.consent_no_engagement} onChange={(e) => setFormData({ ...formData, consent_no_engagement: e.target.checked })} className="w-4 h-4 mt-1 accent-slate-900" />
                <span className="text-sm text-slate-700 leading-relaxed">I understand that submitting this form does not create an engagement.</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="adv-consent-2">
                <input type="checkbox" checked={formData.consent_review} onChange={(e) => setFormData({ ...formData, consent_review: e.target.checked })} className="w-4 h-4 mt-1 accent-slate-900" />
                <span className="text-sm text-slate-700 leading-relaxed">I consent to SAFE Standard reviewing the submitted information for the purpose of preparing a proposal.</span>
              </label>
            </div>

            <div className="pt-2">
              <button type="submit" disabled={submitting} className={`bg-slate-900 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors w-full md:w-auto ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid="adv-submit-btn">
                {submitting ? 'Submitting…' : 'Request Strategic Advisory'}
              </button>
              <p className="text-xs text-slate-500 mt-3">
                Submitting this form does not create an engagement. We review the information provided solely to prepare a tailored proposal.
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default AdvisoryPage;
